import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';

import { Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import readXlsxFile, { type Sheet } from 'read-excel-file/browser';
import { toast } from 'sonner';
import { z } from 'zod';

import type {
  ConversionFormData,
  DelimiterOption,
  ExcelFileData,
  SheetData,
} from '@/types/excelToCsv';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { zodResolver } from '@hookform/resolvers/zod';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
const PREVIEW_ROWS = 10;

const DELIMITER_OPTIONS: DelimiterOption[] = [
  { value: 'comma', label: 'Comma (,)', character: ',' },
  { value: 'pipe', label: 'Pipe (|)', character: '|' },
  { value: 'semicolon', label: 'Semicolon (;)', character: ';' },
];

const conversionSchema = z.object({
  selectedSheet: z.string().min(1, 'Please select a sheet'),
  delimiter: z.enum(['comma', 'pipe', 'semicolon']),
});

const ExcelToCsvConverter = () => {
  const [file, setFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<ExcelFileData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<string[][] | null>(null);

  const form = useForm<ConversionFormData>({
    resolver: zodResolver(conversionSchema),
    defaultValues: {
      selectedSheet: '',
      delimiter: 'comma',
    },
  });

  const selectedSheet = form.watch('selectedSheet');
  const delimiter = form.watch('delimiter');

  // Clear/reset function (defined first to avoid dependency issues)
  const handleClear = useCallback(() => {
    setFile(null);
    setExcelData(null);
    setCsvContent(null);
    setPreviewData(null);
    form.reset();
  }, [form]);

  const parseExcelFile = useCallback(async (file: File): Promise<ExcelFileData> => {
    try {
      const allSheets = await readXlsxFile(file);

      const sheets: SheetData[] = allSheets.map(({ sheet: sheetName, data: rawData }: Sheet) => {
        const filteredData = rawData
          .map((row) =>
            row.map((cell) =>
              cell instanceof Date ? cell.toISOString().slice(0, 10) : (cell?.toString() ?? ''),
            ),
          )
          .filter((row) => row.some((cell) => cell !== ''));

        return {
          sheetName,
          data: filteredData,
          rowCount: filteredData.length,
          columnCount: filteredData.length > 0 ? Math.max(...filteredData.map((r) => r.length)) : 0,
        };
      });

      return {
        fileName: file.name.replace(/\.[^/.]+$/, ''),
        sheets,
        sheetNames: allSheets.map(({ sheet }: Sheet) => sheet),
      };
    } catch {
      throw new Error('Failed to parse Excel file. The file may be corrupted.');
    }
  }, []);

  // Handle file drop/selection
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const uploadedFile = acceptedFiles[0];

      // Validate file size
      if (uploadedFile.size > MAX_FILE_SIZE) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }

      // Validate file type
      if (!ACCEPTED_FILE_TYPES.includes(uploadedFile.type)) {
        toast.error('Invalid file type. Please upload an Excel file (.xlsx or .xls).');
        return;
      }

      setIsProcessing(true);
      setFile(uploadedFile);
      setCsvContent(null);
      setPreviewData(null);

      try {
        const parsedData = await parseExcelFile(uploadedFile);

        if (parsedData.sheets.length === 0) {
          toast.error('No sheets found in Excel file');
          setIsProcessing(false);
          return;
        }

        // Check for empty sheets
        const nonEmptySheets = parsedData.sheets.filter((sheet) => sheet.rowCount > 0);
        if (nonEmptySheets.length === 0) {
          toast.error('All sheets are empty');
          setIsProcessing(false);
          return;
        }

        setExcelData(parsedData);

        // Auto-select first non-empty sheet
        const firstNonEmptySheet = nonEmptySheets[0].sheetName;
        form.setValue('selectedSheet', firstNonEmptySheet);

        toast.success(
          `File loaded successfully! Found ${parsedData.sheets.length} sheet${parsedData.sheets.length > 1 ? 's' : ''}`,
        );
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to process file');
        handleClear();
      } finally {
        setIsProcessing(false);
      }
    },
    [parseExcelFile, form, handleClear],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    multiple: false,
  });

  // Convert to CSV
  const handleConvert = useCallback(
    (data: ConversionFormData) => {
      if (!excelData) return;

      const sheet = excelData.sheets.find((s) => s.sheetName === data.selectedSheet);
      if (!sheet || sheet.data.length === 0) {
        toast.error('Selected sheet is empty');
        return;
      }

      const delimiterChar =
        DELIMITER_OPTIONS.find((d) => d.value === data.delimiter)?.character || ',';

      // Convert to CSV
      const csvLines = sheet.data.map((row) => {
        return row
          .map((cell) => {
            const cellValue = cell?.toString() || '';
            // Escape quotes and wrap in quotes if contains delimiter, quotes, or newlines
            if (
              cellValue.includes(delimiterChar) ||
              cellValue.includes('"') ||
              cellValue.includes('\n')
            ) {
              return `"${cellValue.replace(/"/g, '""')}"`;
            }
            return cellValue;
          })
          .join(delimiterChar);
      });

      const csvString = csvLines.join('\n');
      setCsvContent(csvString);

      // Set preview data (first 10 rows)
      setPreviewData(sheet.data.slice(0, PREVIEW_ROWS));

      toast.success('CSV generated successfully!');
    },
    [excelData],
  );

  // Download CSV
  const handleDownload = useCallback(() => {
    if (!csvContent || !excelData || !selectedSheet) return;

    const fileName = `${excelData.fileName}_${selectedSheet}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Downloaded: ${fileName}`);
  }, [csvContent, excelData, selectedSheet]);

  // Get current sheet data
  const currentSheetData = excelData?.sheets.find((s) => s.sheetName === selectedSheet);

  return (
    <div className='space-y-6'>
      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileSpreadsheet className='h-5 w-5' />
            Upload Excel File
          </CardTitle>
          <CardDescription>
            Upload an Excel file (.xlsx or .xls) to convert to CSV format
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!file ? (
            <div
              {...getRootProps()}
              className={cn(
                'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
                'hover:border-primary hover:bg-primary/5',
                isDragActive && 'border-primary bg-primary/10',
              )}
            >
              <input {...getInputProps()} />
              <Upload className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
              {isDragActive ? (
                <p className='text-lg font-medium'>Drop the file here...</p>
              ) : (
                <>
                  <p className='text-lg font-medium mb-2'>
                    Drag & drop an Excel file here, or click to browse
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    Supports .xlsx and .xls files (max 10MB)
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className='flex items-center justify-between p-4 border rounded-lg bg-muted/50'>
              <div className='flex items-center gap-3'>
                <FileSpreadsheet className='h-8 w-8 text-primary' />
                <div>
                  <p className='font-medium'>{file.name}</p>
                  <p className='text-sm text-muted-foreground'>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                    {excelData &&
                      ` • ${excelData.sheets.length} sheet${excelData.sheets.length > 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <Button variant='ghost' size='icon' onClick={handleClear} disabled={isProcessing}>
                <X className='h-4 w-4' />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration Section */}
      {excelData && (
        <Card>
          <CardHeader>
            <CardTitle>Configure Conversion</CardTitle>
            <CardDescription>Select the sheet and delimiter for your CSV output</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleConvert)} className='space-y-6'>
                {/* Sheet Selection */}
                <FormField
                  control={form.control}
                  name='selectedSheet'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Sheet</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Choose a sheet to convert' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {excelData.sheets.map((sheet) => (
                            <SelectItem key={sheet.sheetName} value={sheet.sheetName}>
                              {sheet.sheetName}
                              <span className='text-muted-foreground ml-2'>
                                ({sheet.rowCount} rows)
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {currentSheetData
                          ? `${currentSheetData.rowCount} rows × ${currentSheetData.columnCount} columns`
                          : 'Select a sheet to see details'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Delimiter Selection */}
                <FormField
                  control={form.control}
                  name='delimiter'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delimiter</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className='flex flex-col space-y-2'
                        >
                          {DELIMITER_OPTIONS.map((option) => (
                            <div key={option.value} className='flex items-center space-x-2'>
                              <RadioGroupItem value={option.value} id={option.value} />
                              <Label htmlFor={option.value} className='cursor-pointer font-normal'>
                                {option.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormDescription>Choose the delimiter for your CSV file</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Convert Button */}
                <Button type='submit' className='w-full' disabled={!selectedSheet || isProcessing}>
                  {isProcessing ? 'Processing...' : 'Generate CSV'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Preview Section */}
      {previewData && currentSheetData && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center justify-between'>
              <span>Data Preview</span>
              <Button onClick={handleDownload} size='sm' className='gap-2'>
                <Download className='h-4 w-4' />
                Download CSV
              </Button>
            </CardTitle>
            <CardDescription>
              Showing first {PREVIEW_ROWS} of {currentSheetData.rowCount} rows with{' '}
              {DELIMITER_OPTIONS.find((d) => d.value === delimiter)?.label.toLowerCase()} delimiter
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className='h-[400px] w-full rounded-md border'>
              <div className='p-4'>
                <table className='w-full border-collapse text-sm'>
                  <thead className='bg-muted sticky top-0'>
                    <tr>
                      <th className='border p-2 font-semibold text-left w-12'>#</th>
                      {previewData[0]?.map((header, colIndex) => (
                        <th key={colIndex} className='border p-2 font-semibold text-left'>
                          {header || `Column ${colIndex + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex} className='hover:bg-muted/50'>
                        <td className='border p-2 text-muted-foreground'>{rowIndex + 1}</td>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className='border p-2'>
                            {cell || <span className='text-muted-foreground italic'>empty</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExcelToCsvConverter;
